import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { engineerService } from '../../services/engineerService';
import { PageHeader, Badge, Button } from '../../components/ui';
import { DocumentCheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  ACTIVE: 'success',
  EXPIRED: 'danger',
  REVOKED: 'danger',
  PENDING_REVIEW: 'warning',
  CHANGES_REQUESTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
};

const EngineerCertificates: React.FC = () => {
  const [tab, setTab] = useState<'pending' | 'submitted'>('pending');
  const { data, isLoading, error } = useQuery({
    queryKey: ['engineer', 'certificates'],
    queryFn: () => engineerService.getMyCertificates(),
    refetchInterval: 60_000,
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
      <PageHeader
        title="Certificates"
        subtitle="Certificates requested for your jobs and everything you've already submitted."
      />

      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab('pending')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'pending'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Needs certificate
          {data?.pending?.length ? (
            <span className="ml-2 inline-flex items-center justify-center text-[11px] font-semibold bg-slate-100 text-slate-700 rounded-full px-1.5 py-0.5">
              {data.pending.length}
            </span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={() => setTab('submitted')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'submitted'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Submitted
          {data?.submitted?.length ? (
            <span className="ml-2 inline-flex items-center justify-center text-[11px] font-semibold bg-slate-100 text-slate-700 rounded-full px-1.5 py-0.5">
              {data.submitted.length}
            </span>
          ) : null}
        </button>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          Couldn't load certificates.
        </div>
      )}

      {tab === 'pending' && data && (
        <div className="space-y-2">
          {data.pending.length === 0 && !isLoading && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
              <DocumentCheckIcon className="w-10 h-10 text-emerald-500 mx-auto" />
              <div className="mt-2 text-sm font-semibold text-slate-700">
                No certificates outstanding
              </div>
              <div className="text-xs text-slate-500 mt-1">
                When admin requests a cert for one of your jobs it'll appear here.
              </div>
            </div>
          )}

          {data.pending.map((p) => (
            <div
              key={p.jobId}
              className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">
                    {formatDate(p.scheduledDate)}
                    {p.jobType ? ` · ${p.jobType}` : ''}
                  </div>
                  <div className="mt-0.5 text-base font-semibold text-slate-900 truncate">
                    {p.jobTitle}
                  </div>
                  <div className="mt-0.5 text-sm text-slate-600 truncate">
                    {p.client?.name || 'No client'}
                    {p.address ? ` · ${p.address}` : ''}
                  </div>
                  {p.requiredSkills && p.requiredSkills.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.requiredSkills.map((s) => (
                        <Badge key={s} tone="neutral">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <Link
                  to={`/engineer/jobs/${p.jobId}/certificates/new`}
                  className="shrink-0"
                >
                  <Button size="sm" rightIcon={<ArrowRightIcon className="w-4 h-4" />}>
                    Start
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'submitted' && data && (
        <div className="space-y-2">
          {data.submitted.length === 0 && !isLoading && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
              <div className="text-sm font-semibold text-slate-700">Nothing submitted yet</div>
              <div className="text-xs text-slate-500 mt-1">
                Certificates you create will show here for easy access.
              </div>
            </div>
          )}

          {data.submitted.map((c) => (
            <div
              key={c.id}
              className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">
                    {c.type} · {c.certificateNumber}
                  </div>
                  <div className="mt-0.5 text-base font-semibold text-slate-900 truncate">
                    {c.job?.title || 'Standalone certificate'}
                  </div>
                  <div className="mt-0.5 text-sm text-slate-600 truncate">
                    {c.client?.name || ''}
                    {c.issueDate ? ` · Issued ${formatDate(c.issueDate)}` : ''}
                    {c.expiryDate ? ` · Expires ${formatDate(c.expiryDate)}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge tone={STATUS_TONE[c.status] || 'neutral'}>
                    {c.status.replace(/_/g, ' ')}
                  </Badge>
                  {c.fileUrl && (
                    <a href={c.fileUrl} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm">
                        PDF
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EngineerCertificates;
